import type { PhotoFileType } from '@shared/types/process';

const PHOTO_TYPE_BY_EXTENSION: Readonly<Record<string, PhotoFileType>> = {
    jpeg: 'Jpeg',
    jpg: 'Jpg',
    png: 'Png',
};

const PHOTO_SIGNATURES: Readonly<Record<'jpeg' | 'png', readonly number[]>> = {
    jpeg: [255, 216, 255],
    png: [137, 80, 78, 71, 13, 10, 26, 10],
};

export const MAX_PHOTO_FIELD_SIZE_BYTES = 200 * 1024 * 1024;

export type PhotoValidationError = 'format' | 'size';

export function getPhotoType(fileName: string): PhotoFileType | null {
    const extension = fileName.split('.').pop()?.toLowerCase();

    return extension ? (PHOTO_TYPE_BY_EXTENSION[extension] ?? null) : null;
}

export function getPhotoAccept(acceptTypes: readonly PhotoFileType[]) {
    return acceptTypes.map((type) => `.${type.toLowerCase()}`).join(',');
}

export async function validatePhotoFile(
    file: File,
    acceptTypes: readonly PhotoFileType[],
    maxSizeMb: number,
): Promise<PhotoValidationError | null> {
    const photoType = getPhotoType(file.name);

    if (
        !photoType ||
        !acceptTypes.includes(photoType) ||
        !(await hasValidPhotoSignature(file, photoType))
    ) {
        return 'format';
    }

    return file.size > maxSizeMb * 1024 * 1024 ? 'size' : null;
}

async function hasValidPhotoSignature(file: File, type: PhotoFileType) {
    const signature =
        type === 'Png' ? PHOTO_SIGNATURES.png : PHOTO_SIGNATURES.jpeg;
    const bytes = new Uint8Array(
        await file.slice(0, signature.length).arrayBuffer(),
    );

    return signature.every((byte, index) => bytes[index] === byte);
}
